/**
 * Sidebar Context
 * Manages sidebar collapsed state across the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      // Use matchMedia so these breakpoints line up EXACTLY with the CSS media queries
      // (e.g. `.main-content { margin-left: 0 }` at max-width:768px). `window.innerWidth`
      // includes the scrollbar while CSS media queries don't, so at the 768px boundary the
      // two disagreed by the scrollbar width: JS said "not mobile" and rendered the desktop
      // sidebar rail, while CSS had already removed the content margin — the rail then
      // overlapped the page. matchMedia removes that mismatch.
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      const tablet = window.matchMedia('(min-width: 769px) and (max-width: 1024px)').matches;
      setIsMobile(mobile);
      setIsTablet(tablet);
      if (mobile) {
        setIsSidebarOpen(false);
        setIsCollapsed(false);
      } else if (tablet) {
        setIsSidebarOpen(true);
        setIsCollapsed(true);
      } else {
        setIsSidebarOpen(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(prev => !prev);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const value = {
    isCollapsed,
    setIsCollapsed,
    isSidebarOpen,
    setIsSidebarOpen,
    isMobile,
    isTablet,
    toggleSidebar,
    closeSidebar,
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export default SidebarContext;
